// weeklyPlanRunner — the plan-week call, detached from the screen.
//
// Rule (no-waiting-page-of-death): generation must survive navigation.
// The WeeklyPlanner screen used to await planWeek() in component
// scope — closing mid-call threw away the paid result. Here the call
// lives at module scope and writes the finished menu straight to the
// weekly_plans draft row when it resolves, mounted or not. Reopening
// the planner finds either the in-flight promise (pendingWeekPlan) or
// the finished slots already on the draft.
//
// weekNote rides in factors.weekNote (jsonb, no migration) so it
// survives reopen alongside the slots.

import { supabase } from "./supabase";
import { planWeek } from "./planWeek";

// plan-week menus land in 10-30s; past this the request is wedged —
// surface a retryable failure instead of spinning forever.
const TIMEOUT_MS = 75_000;

const inFlight = new Map(); // key → promise

const withTimeout = (promise, ms) => {
  let timer;
  return Promise.race([
    promise.finally(() => clearTimeout(timer)),
    new Promise((_, reject) => {
      timer = setTimeout(
        () => reject(new Error("Planning timed out — tap PLAN MY WEEK to retry.")),
        ms,
      );
    }),
  ]);
};

function mergeIntoScaffold(scaffold, resultSlots) {
  const byKey = new Map(resultSlots.map((s) => [`${s.day}:${s.slot}`, s]));
  return scaffold.map((s) => {
    const hit = byKey.get(`${s.day}:${s.slot}`);
    return hit ? { ...s, ...hit } : { ...s };
  });
}

async function persistSlots(draftId, slots, factors, weekNote) {
  const { error } = await supabase
    .from("weekly_plans")
    .update({
      slots,
      ...(factors ? { factors: { ...factors, weekNote: weekNote || "" } } : {}),
    })
    .eq("id", draftId);
  if (error) console.warn("[weeklyPlanRunner] persist failed:", error.message);
}

/** In-flight menu plan for a draft, or null. */
export function pendingWeekPlan(draftId) {
  return inFlight.get(`menu:${draftId}`) || null;
}

/**
 * Run (or join) the full-week menu plan for a draft. Resolves to
 * { slots, weekNote, missing }; the result is ALSO persisted to the
 * draft row regardless of whether any screen is still listening.
 */
export function runWeekPlan({ draftId, args }) {
  const key = `menu:${draftId}`;
  if (inFlight.has(key)) return inFlight.get(key);
  const p = (async () => {
    try {
      const result = await withTimeout(planWeek(args), TIMEOUT_MS);
      const merged = mergeIntoScaffold(args.slots, result.slots);
      await persistSlots(draftId, merged, args.factors, result.weekNote);
      return { slots: merged, weekNote: result.weekNote || "", missing: result.missing || [] };
    } finally {
      inFlight.delete(key);
    }
  })();
  inFlight.set(key, p);
  return p;
}

/** In-flight reroll for a specific slot of a draft, or null. */
export function pendingSlotReroll(draftId, day, slot) {
  return inFlight.get(`reroll:${draftId}:${day}:${slot}`) || null;
}

/**
 * Reroll one slot. `args` is the planWeek reroll payload; currentSlots
 * is the full 21-slot array the replacement merges into. Persists the
 * merged result to the draft row on completion, mounted or not.
 * Resolves to the merged slots array.
 */
export function runSlotReroll({ draftId, day, slot, args, currentSlots }) {
  const key = `reroll:${draftId}:${day}:${slot}`;
  if (inFlight.has(key)) return inFlight.get(key);
  const p = (async () => {
    try {
      const result = await withTimeout(planWeek(args), TIMEOUT_MS);
      const replacement = (result.slots || []).find(
        (s) => s.day === day && s.slot === slot,
      );
      if (!replacement) throw new Error("Reroll came back empty — try again.");
      const merged = currentSlots.map((s) =>
        s.day === day && s.slot === slot
          ? { day, slot, enabled: true, ...replacement }
          : s,
      );
      await persistSlots(draftId, merged, null, null);
      return merged;
    } finally {
      inFlight.delete(key);
    }
  })();
  inFlight.set(key, p);
  return p;
}
